import { cn } from "@/lib/utils";
import { makeAssistantToolUI } from "@assistant-ui/react";

/**
 * Base interface for tool arguments
 */
export interface ToolArguments {}

/**
 * Props for basic tool components
 * @template ToolArgs - Type of arguments passed to the tool
 */
export type ToolComponentProps<ToolArgs = {}> = {
  args: ToolArgs;
  toolName: string;
};

/**
 * Props for components rendering successful tool executions
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 */
export type SuccessToolComponentProps<ToolArgs, ToolResponse> = {
  result: ToolResponse;
  args: ToolArgs;
  toolName: string;
  toolCallId: string;
};

/**
 * Structure for error responses from tools
 * @template T - Type of additional error data
 */
export type ToolErrorResponse = {
  error: string;
};

/**
 * Props for components rendering tool execution errors
 * @template ToolArgs - Type of arguments passed to the tool
 * @template TErrResp - Type of error response data
 */
export type ErrorToolComponentProps<ToolArgs, TErrResp = any> = {
  error: string;
  args: ToolArgs;
  toolName: string;
  result: TErrResp;
};

/**
 * Props for components rendering loading state during tool execution
 */
export type LoadingToolComponentProps = {
  toolName: string;
};

/**
 * Structure of a tool execution result
 * @template ToolResponse - Type of data returned by the tool
 */
export type ToolResult<ToolResponse> = {
  isFirstTool: boolean;
  backendToolName: string;
  data: ToolResponse;
  error: string | undefined;
};

/**
 * Parameters for component renderers
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 */
export type RenderParams<ToolArgs, ToolResponse> = {
  result: ToolResult<ToolResponse>;
  args: ToolArgs;
  toolCallId: string;
  status: { type: string };
  isError?: boolean;
};

/**
 * Creates a renderer for success state components
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 * @param SuccessComponent - React component to render on success
 * @returns Function that renders the success component or null
 */
function createSuccessRenderer<ToolArgs, ToolResponse>(
  SuccessComponent:
    | React.ComponentType<SuccessToolComponentProps<ToolArgs, ToolResponse>>
    | undefined,
) {
  return (params: RenderParams<ToolArgs, ToolResponse>) => {
    const { result, args, toolCallId, status } = params;
    if (SuccessComponent && status.type === "complete") {
      return (
        <SuccessComponent
          result={result.data}
          args={args}
          toolName={result.backendToolName}
          toolCallId={toolCallId}
        />
      );
    }
    return null;
  };
}

/**
 * Creates a renderer for loading state components
 * @template ToolArgs - Type of arguments passed to the tool
 * @param LoadingComponent - React component to render during loading
 * @param toolName - Name of the tool being executed
 * @returns Function that renders the loading component or null
 */
function createLoadingRenderer<ToolArgs>(
  LoadingComponent: React.ComponentType<LoadingToolComponentProps> | undefined,
  toolName: string,
) {
  return (params?: RenderParams<ToolArgs, any>) => {
    if (LoadingComponent) {
      return (
        <LoadingComponent
          toolName={params?.result?.backendToolName || toolName}
        />
      );
    }
    return null;
  };
}

/**
 * Creates a renderer for error state components
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 * @template TErrResp - Type of error response data
 * @param ErrorCustomComponent - React component to render on error
 * @param toolName - Name of the tool that encountered an error
 * @returns Function that renders the error component or null
 */
function createErrorRenderer<
  ToolArgs,
  ToolResponse,
  TErrResp extends Record<string, any>,
>(
  ErrorCustomComponent:
    | React.ComponentType<ErrorToolComponentProps<ToolArgs, TErrResp>>
    | undefined,
  toolName: string,
) {
  return (params: RenderParams<ToolArgs, ToolResponse>) => {
    const { result, args, isError } = params;
    if (ErrorCustomComponent && isError === true) {
      const errorMessage = result.error || "Une erreur inconnue s'est produite";

      const errorData = result.data as unknown as TErrResp;

      return (
        <ErrorCustomComponent
          error={errorMessage}
          args={args}
          toolName={toolName}
          result={errorData}
        />
      );
    }

    if (isError === true) {
      console.error(`Failed to render tool ${toolName}`, { result, args });
    }

    return null;
  };
}

/**
 * Creates a configuration object containing renderers for different component states
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 * @template TErrResp - Type of error response data
 * @param toolName - Name of the tool
 * @param LoadingComponent - Component to render during loading
 * @param SuccessComponent - Component to render on success
 * @param ErrorCustomComponent - Component to render on error
 * @returns Object with renderers for success, loading, and error states
 */
function createComponentConfig<
  ToolArgs extends ToolArguments,
  ToolResponse,
  TErrResp extends Record<string, any>,
>(
  toolName: string,
  LoadingComponent?: React.ComponentType<LoadingToolComponentProps>,
  SuccessComponent?: React.ComponentType<
    SuccessToolComponentProps<ToolArgs, ToolResponse>
  >,
  ErrorCustomComponent?: React.ComponentType<
    ErrorToolComponentProps<ToolArgs, TErrResp>
  >,
) {
  return {
    success: createSuccessRenderer<ToolArgs, ToolResponse>(SuccessComponent),
    loading: createLoadingRenderer<ToolArgs>(LoadingComponent, toolName),
    error: createErrorRenderer<ToolArgs, ToolResponse, TErrResp>(
      ErrorCustomComponent,
      toolName,
    ),
  };
}

/**
 * Determines which component type to render based on the current state
 * @param isError - Whether an error has occurred
 * @param status - Current status of the tool execution
 * @param result - The result of the tool execution
 * @returns String identifying the component type to render ("error", "loading", or "success")
 */
function determineComponentType(
  isError: boolean | undefined,
  status: { type: string },
  result?: ToolResult<any> | null,
) {
  if (isError === true) {
    return "error";
  }

  if (!result || result.data === null || result.data === undefined) {
    return "loading";
  }

  return status.type === "running" ? "loading" : "success";
}

/**
 * Creates parameters for each component type renderer
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 * @param safeResult - Tool execution result
 * @param args - Arguments passed to the tool
 * @param toolCallId - ID of the tool call
 * @param status - Current status of the tool execution
 * @param isError - Whether an error has occurred
 * @returns Object with parameters for each component type
 */
function createParamsByType<ToolArgs, ToolResponse>(
  safeResult: ToolResult<ToolResponse>,
  args: ToolArgs,
  toolCallId: string,
  status: { type: string },
  isError?: boolean,
) {
  return {
    success: { result: safeResult, args, toolCallId, status, isError },
    loading: { result: safeResult, args, status, toolCallId, isError },
    error: { result: safeResult, args, isError, status, toolCallId },
  };
}

/**
 * Factory function for creating assistant tool UI components with customizable rendering
 * @template ToolArgs - Type of arguments passed to the tool
 * @template ToolResponse - Type of response returned by the tool
 * @template TErrResp - Type of error response data
 * @param options - Configuration options
 * @param options.toolName - Name of the tool
 * @param options.LoadingComponent - Component to render during loading
 * @param options.SuccessComponent - Component to render on success
 * @param options.ErrorCustomComponent - Component to render on error
 * @returns Assistant tool UI component
 */
export function FactoryMakeAssistantToolUI<
  ToolArgs extends ToolArguments,
  ToolResponse = {},
  TErrResp extends Record<string, any> = {},
>({
  toolName,
  LoadingComponent,
  SuccessComponent,
  ErrorCustomComponent,
}: {
  toolName: string;
  LoadingComponent?: React.ComponentType<LoadingToolComponentProps>;
  SuccessComponent?: React.ComponentType<
    SuccessToolComponentProps<ToolArgs, ToolResponse>
  >;
  ErrorCustomComponent?: React.ComponentType<
    ErrorToolComponentProps<ToolArgs, TErrResp>
  >;
}) {
  const emptyResult: ToolResult<ToolResponse> = {
    isFirstTool: false,
    backendToolName: toolName,
    data: {} as ToolResponse,
    error: undefined,
  };

  const componentConfig = createComponentConfig<
    ToolArgs,
    ToolResponse,
    TErrResp
  >(toolName, LoadingComponent, SuccessComponent, ErrorCustomComponent);

  return makeAssistantToolUI<ToolArgs, ToolResult<ToolResponse>>({
    toolName,
    render: ({ args, result, status, toolCallId, isError }) => {
      const componentType = determineComponentType(isError, status, result);
      const safeResult = result || emptyResult;
      const paramsByType = createParamsByType<ToolArgs, ToolResponse>(
        safeResult,
        args,
        toolCallId,
        status,
        isError,
      );

      const isFirstTool = result ? result.isFirstTool : false;

      return (
        <div className={cn(isFirstTool ? "inline-block" : "inline")}>
          {componentConfig[componentType](paramsByType[componentType])}
        </div>
      );
    },
  });
}
